import React, { useEffect } from 'react'
import { IonIcon, IonLabel, IonModal, IonText } from '@ionic/react'
import { chevronBackOutline } from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import NumberPad from '../../../components/numberPad/NumberPad.component'
import ActionButtons from '../../../components/actionButtons/ActionButtons.component'
import { getDisplayName } from '../resume/utils/sessionResume.util'
import Logger from '../../../services/logger.service'
import './SessionPinModal.css'

interface DisplayUser {
  firstName?: string | null
  lastName?: string | null
  userName?: string
}

interface SessionPinModalProps {
  isOpen: boolean
  displayUser?: DisplayUser
  pinValues: string[]
  errorMessage: string
  isResuming: boolean
  onPinChange: (numbers: string[]) => void
  onResume: () => void
  onStartNewSession: () => void
}

export const SessionPinModal: React.FC<SessionPinModalProps> = ({
  isOpen,
  displayUser,
  pinValues,
  errorMessage,
  isResuming,
  onPinChange,
  onResume,
  onStartNewSession,
}) => {
  useEffect(() => {
    if (!isOpen) return
    ;["ion-modal[data-testid='NumberPadModal']", "ion-popover[data-testid='NumberPadPopover']"].forEach((selector) => {
      document.querySelectorAll(selector).forEach((el) => {
        void (el as HTMLIonModalElement | HTMLIonPopoverElement).dismiss()
      })
    })

    document.body.classList.add('session-resume-active')
    return () => document.body.classList.remove('session-resume-active')
  }, [isOpen])

  const { t } = useTranslation()
  const { firstName, lastName, userName } = displayUser ?? {}
  const displayName = getDisplayName(firstName, lastName, userName)
  const pin = pinValues.join('')
  const isResumeDisabled = !pin || pin.trim() === '' || isResuming
  const isAllDisabled = isResuming

  const getActionButtonObj = () => {
    const resumeButton = {
      label: isResuming ? t('sessionResume.resuming') : t('sessionResume.resume'),
      onClick: () => {
        Logger.info('SessionPin resume triggered via Button')
        onResume()
      },
      disabled: isResumeDisabled,
      className: 'btn btn-primary',
    }

    return [resumeButton]
  }

  const renderHeading = (): React.ReactNode => {
    return <div className='info-heading'>{t('sessionResume.heading')}</div>
  }

  const renderUsername = (): React.ReactNode => {
    if (!displayName) return null
    return <strong className='session-resume-username-text'>{displayName}</strong>
  }

  const renderContent = (): React.ReactNode => {
    return (
      <div className='session-resume-content'>
        {displayName.length > 0 && (
          <IonText>
            <IonLabel className='session-resume-username'>{renderUsername()}</IonLabel>
          </IonText>
        )}

        <NumberPad
          dataTestId='SessionPin'
          type='password'
          values={pinValues}
          label={t('sessionResume.enterPinLabel')}
          onNumPadClick={onPinChange}
          disabled={isAllDisabled}
          onKeyPress={(e) => {
            if (e.key === 'Enter' && !isResumeDisabled) {
              Logger.info('SessionPin resume triggered via Enter')
              onResume()
            }
          }}
        />
        {errorMessage && (
          <IonText>
            <p className='session-resume-error'>{errorMessage}</p>
          </IonText>
        )}
      </div>
    )
  }

  const renderActionButtons = (): React.ReactNode => {
    return (
      <div className='mt-20'>
        <ActionButtons actionButtonObj={getActionButtonObj()} />
      </div>
    )
  }

  const renderNewSessionLink = (): React.ReactNode => {
    return (
      <div className='d-flex align-items-center session-resume-new-session'>
        <IonIcon color='primary' icon={chevronBackOutline} />
        <button onClick={onStartNewSession} disabled={isAllDisabled} className='session-resume-new-session-link'>
          {t('sessionResume.startNewSession')}
        </button>
      </div>
    )
  }

  return (
    <IonModal
      id='popup-container'
      isOpen={isOpen}
      className='session-resume-modal popup-container'
      backdropDismiss={false}>
      <>
        {renderHeading()}
        {renderContent()}
        {renderActionButtons()}
        {renderNewSessionLink()}
      </>
    </IonModal>
  )
}
